// Shopify App Bridge utilities for embedded app functionality

interface ShopifyGlobal {
    idToken?: () => Promise<string>;
    config?: (options: { apiKey: string; host: string }) => void;
    redirect?: (url: string) => void;
    app?: {
        idToken?: () => Promise<string>;
    };
}

declare global {
    interface Window {
        shopify?: ShopifyGlobal;
    }
}

const persistEmbeddedContext = (shop: string | null, host: string | null) => {
    if (shop) {
        sessionStorage.setItem('shopify_shop', shop);
        localStorage.setItem('shopify_shop_domain', shop);
    }
    if (host) {
        sessionStorage.setItem('shopify_host', host);
    }
};

const getEmbeddedParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const shop = urlParams.get('shop') || sessionStorage.getItem('shopify_shop');
    const host = urlParams.get('host') || sessionStorage.getItem('shopify_host');
    const embedded = urlParams.get('embedded');

    if (urlParams.get('shop') || urlParams.get('host')) {
        persistEmbeddedContext(urlParams.get('shop'), urlParams.get('host'));
    }

    return { shop, host, embedded };
};

export const isEmbeddedApp = (): boolean => {
    if (typeof window === 'undefined') return false;
    const { shop, host } = getEmbeddedParams();
    return !!(shop && host);
};

export const isAppBridgeAvailable = (): boolean => {
    return typeof window !== 'undefined' &&
        !!(window.shopify && (typeof window.shopify.idToken === 'function' || window.shopify.app));
};

const waitForAppBridge = (): Promise<ShopifyGlobal> => {
    return new Promise((resolve, reject) => {
        if (window.shopify) {
            resolve(window.shopify);
            return;
        }

        let attempts = 0;
        const maxAttempts = 50;

        const checkShopify = () => {
            attempts++;
            if (window.shopify) {
                resolve(window.shopify);
            } else if (attempts >= maxAttempts) {
                reject(new Error('App Bridge not loaded - ensure script is in HTML head'));
            } else {
                setTimeout(checkShopify, 100);
            }
        };

        checkShopify();
    });
};

export const getShopifySessionToken = async (): Promise<string> => {
    if (!isEmbeddedApp()) {
        throw new Error('Session tokens only available in embedded mode');
    }

    if (!window.shopify) {
        await waitForAppBridge();
    }

    if (!window.shopify) {
        throw new Error('App Bridge not available');
    }

    let token: string | undefined;

    if (typeof window.shopify.idToken === 'function') {
        token = await window.shopify.idToken();
    } else if (window.shopify.app && typeof window.shopify.app.idToken === 'function') {
        token = await window.shopify.app.idToken();
    } else {
        throw new Error('Session token method not available');
    }

    if (!token) {
        throw new Error('Failed to retrieve session token');
    }

    return token;
};

export const getShopDomain = (): string | null => {
    const { shop } = getEmbeddedParams();
    if (shop) {
        return shop.replace(/^https?:\/\//, '').split('/')[0];
    }
    return localStorage.getItem('shopify_shop_domain');
};

export default {
    isEmbeddedApp,
    isAppBridgeAvailable,
    getShopifySessionToken,
    getShopDomain
};
